"use client"

import { cn } from "@/lib/utils"

import classes from "./status-badge.module.scss"

export type StatusBadgeVariant = "live" | "ended" | "upcoming" | "all-awarded"
/** Fixed sizes, or `responsive` (sm mobile / md tablet / lg desktop). */
export type StatusBadgeSize = "sm" | "md" | "lg" | "responsive"

export type StatusBadgeProps = {
  /** Visual status style */
  variant?: StatusBadgeVariant
  /**
   * Badge scale — defaults to `responsive`:
   * small on mobile, medium on tablet (≥769), large on desktop (≥1025).
   */
  size?: StatusBadgeSize
  /** Override label — defaults to the variant name */
  children?: React.ReactNode
  className?: string
}

const VARIANT_CLASS: Record<StatusBadgeVariant, string> = {
  live: classes.live,
  ended: classes.ended,
  upcoming: classes.upcoming,
  "all-awarded": classes.allAwarded,
}

const SIZE_CLASS: Record<StatusBadgeSize, string> = {
  sm: classes.sizeSm,
  md: classes.sizeMd,
  lg: classes.sizeLg,
  responsive: classes.sizeResponsive,
}

const DEFAULT_LABEL: Record<StatusBadgeVariant, string> = {
  live: "LIVE",
  ended: "ENDED",
  upcoming: "UPCOMING",
  "all-awarded": "ALL AWARDED",
}

/**
 * StreamHub status pill badge (LIVE / ENDED / UPCOMING / ALL AWARDED).
 * LIVE uses a CSS pulse on the center dot (+ label shimmer), scaled by `--k`.
 * Default size follows viewport: sm → md → lg.
 */
export function StatusBadge({
  variant = "live",
  size = "responsive",
  children,
  className,
}: StatusBadgeProps) {
  const label = children ?? DEFAULT_LABEL[variant]
  const showDot = variant === "live"
  const labelText = typeof label === "string" ? label : DEFAULT_LABEL[variant]

  return (
    <span
      data-slot="status-badge"
      data-variant={variant}
      data-size={size}
      className={cn(
        classes.root,
        SIZE_CLASS[size],
        VARIANT_CLASS[variant],
        className
      )}
    >
      {showDot ? <span className={classes.dot} aria-hidden /> : null}
      <span
        className={classes.label}
        data-text={showDot ? labelText : undefined}
      >
        {label}
      </span>
    </span>
  )
}
